import dayjs from '../../../utils/dayjsConfig'
import { formatTimetoHHmm } from '../../../helpers/utils'

export const MAIN_HEADERS_BY_VARIANT = {
  unified: [
    { label: 'Ampliar', description: 'Muestra el detalle consolidado de la visita.' },
    { label: 'Cliente', description: 'Código y nombre del cliente.' },
    { label: 'Fecha planificada', description: 'Día en que debía realizarse la visita.' },
    { label: 'Fecha de ejecución', description: 'Día en que realmente se ejecutó la visita.' },
    {
      label: 'Duración de visita/atención',
      description: 'Duración total registrada para la visita del cliente.'
    },
    { label: 'Efectividad', description: 'Indica si la visita tuvo actividad efectiva.' },
    {
      label: 'Actividades realizadas',
      description: 'Actividades registradas para la visita en ejecución.'
    },
    { label: 'Estado', description: 'Situación actual de la visita.' }
  ],
  route: [
    { label: 'Ampliar', description: 'Muestra el detalle consolidado de la visita.' },
    { label: 'Cliente', description: 'Código y nombre del cliente.' },
    { label: 'Fecha planificada', description: 'Día en que debía realizarse la visita.' },
    { label: 'Fecha de ejecución', description: 'Día en que realmente se ejecutó la visita.' },
    {
      label: 'Duración de visita/atención',
      description: 'Duración total registrada para la visita del cliente.'
    },
    {
      label: 'Dentro de ruta',
      description: 'Indica si la visita se realizó dentro de la planificación esperada.'
    },
    { label: 'Efectividad', description: 'Indica si la visita tuvo actividad efectiva.' },
    {
      label: 'Actividades realizadas',
      description: 'Actividades registradas para la visita en ejecución.'
    },
    { label: 'Estado', description: 'Situación actual de la visita.' }
  ]
}

export const DETAIL_HEADERS = [
  {
    label: 'Fecha y hora',
    description: 'Fecha y hora registrada para el detalle de la visita.'
  },
  {
    label: 'Código de actividad',
    description: 'Código asociado al detalle de actividad o marca temporal.'
  },
  {
    label: 'Actividad',
    description: 'Actividad registrada durante la visita.'
  },
  {
    label: 'Motivo/Descripción',
    description: 'Motivo o descripción capturada para la actividad.'
  },
  {
    label: 'Monto',
    description: 'Monto asociado a la actividad cuando aplique.',
    alignClassName: 'align-center-table-column',
    justifyHeader: true
  },
  {
    label: 'Dentro de geocerca',
    description: 'Indica si el detalle fue registrado dentro de la geocerca.',
    alignClassName: 'align-center-table-column',
    justifyHeader: true
  },
  {
    label: 'Ubicación',
    description: 'Abre el mapa asociado al detalle.',
    alignClassName: 'align-center-table-column',
    justifyHeader: true
  }
]

export const CENTER_ALIGNED_HEADERS = new Set([
  'Ampliar',
  'Dentro de ruta',
  'Efectividad',
  'Estado'
])

export const EMPTY_DIALOG_STATE = {
  open: false,
  type: null,
  payload: null,
  title: '',
  subtitleLines: [],
  mobileDetails: null
}

export const formatDateLabel = (value) => {
  if (!value) return '-'
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-'
}

export const formatDateTimeLabel = (value) => {
  if (!value) return '-'
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('DD/MM/YYYY - hh:mm A') : '-'
}

export const formatTimeLabel = (value) => {
  if (!value) return '-'
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('hh:mm A') : '-'
}

export const getDialogDetailsTitle = (dialogType) => {
  if (dialogType === 'activity') {
    return 'Detalles de captura de actividad'
  }

  if (dialogType === 'timestamp') {
    return 'Detalles de captura de cliente'
  }

  return null
}

export const buildTimestampDialogState = ({ model, fallbackName, detailRow, salesmanName }) => {
  const clientName = model.client_name || fallbackName || '-'
  const dateValue = model.visited_date || model.visit_date || model.execution_date
  const subtitleLines = [`Cliente: ${clientName}`, `Fecha: ${formatDateLabel(dateValue)}`]
  const normalizedPayload = {
    ...model,
    client_name: clientName,
    visited_date: dateValue,
    capture_activity_name: detailRow?.label || 'Control de horario'
  }

  if (detailRow?.type === 'check_in' || detailRow?.type === 'check_out') {
    subtitleLines.push(
      `${detailRow.label || 'Fecha y hora'}: ${formatDateTimeLabel(detailRow.datetime)}`
    )
  }

  return {
    open: true,
    type: 'timestamp',
    payload: normalizedPayload,
    title: `Reporte: ${salesmanName}`,
    subtitleLines,
    mobileDetails: {
      eyebrow: 'Detalle de horario',
      title: `Reporte: ${salesmanName}`,
      clientName,
      date: formatDateLabel(dateValue),
      startTime: formatTimetoHHmm(model.start_visit_time) || '-',
      endTime:
        model.status === 'completed' ? formatTimetoHHmm(model.end_visit_time) || '-' : 'En curso',
      capturedTime:
        detailRow?.type === 'check_in' || detailRow?.type === 'check_out'
          ? `${detailRow.label || 'Fecha y hora'}: ${formatDateTimeLabel(detailRow.datetime)}`
          : ''
    }
  }
}

export const buildActivityDialogState = ({
  model,
  fallbackName,
  salesmanName,
  formatActivityName
}) => {
  const clientName = model.client_name || fallbackName || '-'
  const dateTimeValue = model.created_date || model.datetime || model.date_time
  const activityName = formatActivityName
    ? formatActivityName(model.activity_name)
    : model.activity_name || '-'
  const normalizedPayload = {
    ...model,
    client_name: clientName,
    created_date: dateTimeValue
  }

  return {
    open: true,
    type: 'activity',
    payload: normalizedPayload,
    title: `Reporte: ${salesmanName}`,
    subtitleLines: [
      `Cliente: ${clientName}`,
      `Fecha y hora: ${formatDateTimeLabel(dateTimeValue)}`,
      `Actividad: ${activityName}`
    ],
    mobileDetails: {
      eyebrow: 'Detalle de actividad',
      title: `Reporte: ${salesmanName}`,
      clientName,
      date: formatDateLabel(dateTimeValue),
      time: formatTimeLabel(dateTimeValue),
      activityName
    }
  }
}
